import {
  Users,
  UserCheck,
  Gift,
  BarChart3,
  Newspaper,
  Calendar,
  CalendarHeart,
  MessageSquare,
  FolderOpen,
  StickyNote,
  FileCheck,
  Ticket,
  ClipboardList,
  KanbanSquare,
  Video,
  MessageCircle,
  Mail,
  Settings,
  Layers,
  Dices,
  Store,
  Megaphone,
  HandCoins,
  Receipt,
  Calculator,
  RefreshCw,
  HeartHandshake,
  KeyRound,
  BadgePercent,
  Gamepad2,
  Banknote,
  Handshake,
  MessageSquareText,
} from "lucide-react";
import type { PlanFeatures } from "@/shared/types/planFeatures";
import { getContentFeatureAvailability, isContentFeatureAvailable } from "@/shared/utils/contentFeatureAvailability.utils";
import type { UpgradeFeatureItem } from "../types/upsellPage.types";

// Premium-exclusive feature flags. Used by:
//   - UpgradeSection (visual highlight via feature.tier === "premium")
//   - FeatureProtectedRoute & navigation.utils to decide whether a locked
//     feature routes to the Standard or Premium upsell.
// Keep in sync with Excel "Tableau des packs Upsell.xlsx" extras Premium.
export const PREMIUM_FEATURE_KEYS = new Set([
  "service_yeati_loc",
  "service_deals",
  "service_bridge",
  "service_topup",
  "service_pronostic",
] as const);

// Feature catalogue, ordered to match the Excel display order so the
// Standard and Premium columns render in the documented sequence.
// Items without a featureKey are static labels (no backend gating).
export const UPGRADE_FEATURES: UpgradeFeatureItem[] = [
  // ── Start tier ─────────────────────────────────────────────────
  {
    featureKey: "service_representatives",
    label: "Gestion des élus",
    icon: UserCheck,
    tier: "start",
  },
  {
    featureKey: "service_employees",
    label: "Gestion des salariés",
    icon: Users,
    tier: "start",
  },
  {
    featureKey: "service_vouchers",
    label: "Titres cadeaux",
    icon: Gift,
    tier: "start",
  },

  // ── Standard tier ──────────────────────────────────────────────
  // No matching backend feature_code today — static label.
  {
    label: "Subventions",
    icon: HandCoins,
    tier: "standard",
  },
  {
    featureKey: "service_refund",
    label: "Remboursements",
    icon: Receipt,
    tier: "standard",
  },
  // No matching backend feature_code today — static label.
  {
    label: "Comptabilité",
    icon: Calculator,
    tier: "standard",
  },
  {
    featureKey: "service_sms",
    label: "Envoi de SMS",
    icon: Mail,
    tier: "standard",
  },
  {
    featureKey: "service_exchanges",
    label: "Discussions",
    icon: MessageSquare,
    tier: "standard",
  },
  {
    featureKey: "service_blog",
    label: "Blog",
    icon: Newspaper,
    tier: "standard",
  },
  {
    featureKey: "service_polls",
    label: "Sondages",
    icon: BarChart3,
    tier: "standard",
  },
  {
    featureKey: "service_surveys",
    label: "Enquêtes",
    icon: ClipboardList,
    tier: "standard",
  },
  {
    featureKey: "service_calendar",
    label: "Calendrier",
    icon: Calendar,
    tier: "standard",
  },
  {
    featureKey: "service_newsletter",
    label: "Newsletter",
    icon: Newspaper,
    tier: "standard",
  },
  {
    featureKey: "service_cse_messages",
    label: "Messages d'accueil",
    icon: MessageSquareText,
    tier: "standard",
  },
  {
    featureKey: "service_pinnednotes",
    label: "Notes épinglées",
    icon: StickyNote,
    tier: "standard",
  },
  {
    featureKey: "service_ticketing",
    label: "Billetterie",
    icon: Ticket,
    tier: "standard",
  },
  {
    featureKey: "service_drive",
    label: "Gestionnaire de fichiers",
    icon: FolderOpen,
    tier: "standard",
  },
  {
    featureKey: "service_chat",
    label: "Chat en temps réel",
    icon: MessageCircle,
    tier: "standard",
  },
  {
    featureKey: "service_visio",
    label: "Visioconférence",
    icon: Video,
    tier: "standard",
  },
  {
    featureKey: "service_task_manager",
    label: "Gestionnaire de projet",
    icon: KanbanSquare,
    tier: "standard",
  },
  {
    featureKey: "service_openquestions",
    label: "Assistant IA",
    icon: MessageCircle,
    tier: "standard",
  },
  {
    featureKey: "custom_forms",
    label: "Formulaires personnalisés",
    icon: ClipboardList,
    tier: "standard",
  },
  {
    featureKey: "rules_engine",
    label: "Moteur de règles",
    icon: Settings,
    tier: "standard",
  },
  {
    featureKey: "service_dashboard_layouts",
    label: "Tableaux de bord personnalisés",
    icon: Layers,
    tier: "standard",
  },
  {
    featureKey: "service_beneficiaries",
    label: "Ayants droit",
    icon: Users,
    tier: "standard",
  },
  {
    featureKey: "service_proof_document",
    label: "Justificatifs",
    icon: FileCheck,
    tier: "standard",
  },
  {
    featureKey: "service_lottery",
    label: "Tombola",
    icon: Dices,
    tier: "standard",
  },
  {
    featureKey: "service_eshop",
    label: "Boutique",
    icon: Store,
    tier: "standard",
  },
  {
    featureKey: "service_classifieds",
    label: "Petites annonces",
    icon: Handshake,
    tier: "standard",
  },
  {
    featureKey: "service_changelog",
    label: "Nouveautés",
    icon: Megaphone,
    tier: "standard",
  },

  // ── Premium tier (Excel "+ extras") ────────────────────────────
  {
    featureKey: "service_bridge",
    label: "Comptabilité synchronisée",
    icon: RefreshCw,
    tier: "premium",
  },
  // No backend feature_code — static label.
  {
    label: "Accompagnement sur mesure",
    icon: HeartHandshake,
    tier: "premium",
  },
  {
    featureKey: "service_yeati_loc",
    label: "Location",
    icon: KeyRound,
    tier: "premium",
  },
  {
    featureKey: "service_deals",
    label: "Bons plans",
    icon: BadgePercent,
    tier: "premium",
  },
  {
    featureKey: "service_pronostic",
    label: "Jeux intégrés",
    icon: Gamepad2,
    tier: "premium",
  },
  {
    featureKey: "service_topup",
    label: "Complément bancaire des cartes cadeaux",
    icon: Banknote,
    tier: "premium",
  },
  {
    featureKey: "service_advent_calendar",
    label: "Calendrier des jeux",
    icon: CalendarHeart,
    tier: "premium",
  },
];

// ─── Plan feature lists (tier-driven) ────────────────────────────
export const START_PLAN_FEATURES: UpgradeFeatureItem[] = UPGRADE_FEATURES.filter(
  (f) => f.tier === "start",
);

export const STANDARD_PLAN_FEATURES: UpgradeFeatureItem[] = UPGRADE_FEATURES.filter(
  (f) => f.tier === "start" || f.tier === "standard",
);

export const PREMIUM_PLAN_FEATURES: UpgradeFeatureItem[] = UPGRADE_FEATURES;

/** Only these mutually exclusive services are excluded from commercial offers. */
export const filterAvailableUpgradeFeatures = (items: UpgradeFeatureItem[], features?: Partial<PlanFeatures>): UpgradeFeatureItem[] =>
  items.filter((item) => isContentFeatureAvailable(item.featureKey ?? "", getContentFeatureAvailability(features)));
